import { z } from 'zod';
import { MILK_SESSIONS, WITHHELD_REASONS } from '../enums';
import { clientCreatedAt, isoDate, optText, uuid, type SyncFields } from './common';

/** MR-1, MR-3. One record per cow, date and session (deterministic id, D-13). */
export const milkRecordSchema = z
  .object({
    animalId: uuid,
    date: isoDate,
    session: z.enum(MILK_SESSIONS),
    ml: z.number().int().min(0).max(40_000),
    withheld: z.boolean().default(false),
    withheldReason: z.enum(WITHHELD_REASONS).nullish(),
    /** Withheld note (reason OTHER); also used for the "Why no milk?" note when ml = 0 (see docs/questions.md Q-03). */
    withheldNote: optText(200),
    clientCreatedAt,
  })
  .superRefine((r, ctx) => {
    if (r.withheld && !r.withheldReason)
      ctx.addIssue({ code: 'custom', path: ['withheldReason'], message: 'Required' });
    if (r.withheld && r.withheldReason === 'OTHER' && !r.withheldNote)
      ctx.addIssue({ code: 'custom', path: ['withheldNote'], message: 'Required' });
    if (!r.withheld && r.withheldReason)
      ctx.addIssue({ code: 'custom', path: ['withheldReason'], message: 'Only for withheld milk' });
  });

export type MilkRecordData = z.output<typeof milkRecordSchema>;
export type MilkRecordRecord = MilkRecordData & SyncFields & { lateEntry?: boolean };

/** MR-4. One record per date (deterministic id, D-13). */
export const dailyMilkUsageSchema = z.object({
  date: isoDate,
  calfMl: z.number().int().min(0).max(200_000).default(0),
  homeMl: z.number().int().min(0).max(200_000).default(0),
  spoiledMl: z.number().int().min(0).max(200_000).default(0),
  note: optText(500),
  clientCreatedAt,
});

export type DailyMilkUsageData = z.output<typeof dailyMilkUsageSchema>;
export type DailyMilkUsageRecord = DailyMilkUsageData & SyncFields;
